import { BillingCycleToggle, SeatStepper, formatMoney } from "../components";

const BILLING_CYCLE_MAP = {
  monthly:   { period: 1, period_unit: "month" },
  quarterly: { period: 3, period_unit: "month" },
  annually:  { period: 1, period_unit: "year" },
};

function findMatchingPrice(plan_prices, billingCycle) {
  if (!plan_prices || !plan_prices.length) return null;
  const { period, period_unit } = BILLING_CYCLE_MAP[billingCycle] || BILLING_CYCLE_MAP.monthly;
  return plan_prices.find((p) => p.period === period && p.period_unit === period_unit) || null;
}

function EditPlan({ availablePlans, selectedPlanName, billingCycle, seats, pricePerSeat, planPricingModel, onPlanChange, onBillingCycleChange, onSeatsChange, onPricePerSeatChange }) {
  const selectedPlan = availablePlans.find((p) => p.plan_name === selectedPlanName) || null;
  const selectedPrice = selectedPlan ? findMatchingPrice(selectedPlan.plan_prices, billingCycle) : null;
  const isPerUnit = planPricingModel === "per_unit";

  const subtotal = pricePerSeat != null
    ? (isPerUnit ? pricePerSeat * seats : pricePerSeat)
    : null;

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</h3>
      </div>
      <div className="p-5 space-y-4">

        {/* Plan dropdown + Billing cycle */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Plan Type</label>
            <div className="relative">
              <select
                value={selectedPlanName || ""}
                onChange={(e) => onPlanChange(e.target.value)}
                className="w-full h-9 px-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              >
                <option value="" disabled>Select a plan</option>
                {availablePlans
                  .filter((plan) => findMatchingPrice(plan.plan_prices, billingCycle))
                  .map((plan) => (
                    <option key={plan.plan_name} value={plan.plan_name}>
                      {plan.plan_name}
                    </option>
                  ))}
              </select>
              <svg
                xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                aria-hidden="true"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Billing Cycle</label>
            <BillingCycleToggle value={billingCycle} onChange={onBillingCycleChange} />
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Price row */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-900">{selectedPlanName || "—"}</span>
            {selectedPrice && (
              <span className="text-xs text-gray-400">${formatMoney(selectedPrice.price)}{isPerUnit ? "/seat" : " flat"}</span>
            )}
            {selectedPlan && !selectedPrice && (
              <span className="text-xs text-amber-500">No price for selected billing cycle</span>
            )}
          </div>

          <div className="grid gap-3 grid-cols-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                Seats
              </label>
              <SeatStepper value={seats} onChange={onSeatsChange} min={1} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                {isPerUnit ? "Price/Seat" : "Flat Price"}
              </label>
              <div className="h-9 flex items-center px-3 bg-white border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400">
                <span className="text-sm text-gray-400 mr-0.5">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricePerSeat ?? ""}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    onPricePerSeatChange(isNaN(val) ? null : val);
                  }}
                  disabled={!selectedPrice}
                  className="flex-1 text-sm text-gray-900 border-0 focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:text-gray-400"
                  placeholder="—"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                Subtotal
              </label>
              <div className="h-9 flex items-center justify-end px-3 bg-gray-50 border border-gray-100 rounded-lg">
                <span className="text-sm font-bold text-gray-900">
                  {subtotal != null ? `$${formatMoney(subtotal)}` : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

export default EditPlan;
